import { listen } from "@fluorescent/dom";

const selectors = {
  optionInput: '[name^="options"]',
  quantityInput: `[data-quantity-input][id^="quantity-input-product-form"]`,
  formQuantity: '[name="quantity"]',
  propertyInput: '[name^="properties"]',
};

// ProductForm is variant-agnostic. Handlers receive serialized
// { options, properties, quantity }; callers resolve variants separately.
export default function ProductForm(container, form, config = {}) {
  const listeners = [];

  const getOptions = () => {
    return _serializeOptionValues(optionInputs, function (item) {
      var regex = /(?:^(options\[))(.*?)(?:\])/;
      item.name = regex.exec(item.name)[2]; // Use just the value between 'options[' and ']'
      return item;
    });
  };

  const getProperties = () => {
    const properties = _serializePropertyValues(
      propertyInputs,
      function (propertyName) {
        var regex = /(?:^(properties\[))(.*?)(?:\])/;
        var name = regex.exec(propertyName)[2]; // Use just the value between 'properties[' and ']'
        return name;
      }
    );

    return Object.entries(properties).length === 0 ? null : properties;
  };

  const getQuantity = () => {
    return formQuantityInput[0]
      ? Number.parseInt(formQuantityInput[0].value, 10)
      : 1;
  };

  const getProductFormEventData = () => ({
    options: getOptions(),
    properties: getProperties(),
    quantity: getQuantity(),
  });

  const onFormEvent = cb => {
    if (typeof cb === "undefined") return;

    return event => {
      event.dataset = getProductFormEventData();
      cb(event);
    };
  };

  const initInputs = (selector, cb) => {
    const elements = [...container.querySelectorAll(selector)];

    return elements.map(element => {
      listeners.push(listen(element, "change", onFormEvent(cb)));
      return element;
    });
  };

  // Option-input listeners are tracked separately so they can be torn down and
  // re-bound when the variant picker is swapped in from a section response.
  let optionInputListeners = [];
  let optionInputs = [];

  const initOptionInputs = () => {
    const elements = [...container.querySelectorAll(selectors.optionInput)];
    optionInputs = elements;
    optionInputListeners = elements.map(element =>
      listen(element, "change", onFormEvent(config.onOptionChange))
    );
  };

  const refreshOptionInputs = () => {
    optionInputListeners.forEach(unsubscribe => unsubscribe());
    initOptionInputs();
  };

  listeners.push(listen(form, "submit", onFormEvent(config.onFormSubmit)));
  initOptionInputs();

  const formQuantityInput = initInputs(
    selectors.quantityInput,
    config.onQuantityChange
  );
  const propertyInputs = initInputs(
    selectors.propertyInput,
    config.onPropertyChange
  );

  const destroy = () => {
    listeners.forEach(unsubscribe => unsubscribe());
    optionInputListeners.forEach(unsubscribe => unsubscribe());
  };

  return { destroy, refreshOptionInputs };
}

function _serializeOptionValues(inputs, transform) {
  return inputs.reduce(function (options, input) {
    if (
      input.checked || // If input is a checked (means type radio or checkbox)
      (input.type !== "radio" && input.type !== "checkbox") // Or if its any other type of input
    ) {
      options.push(transform({ name: input.name, value: input.value }));
    }

    return options;
  }, []);
}

function _serializePropertyValues(inputs, transform) {
  return inputs.reduce(function (properties, input) {
    if (
      input.checked || // If input is a checked (means type radio or checkbox)
      (input.type !== "radio" && input.type !== "checkbox") // Or if its any other type of input
    ) {
      properties[transform(input.name)] = input.value;
    }

    return properties;
  }, {});
}
